import React, { useEffect } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { useCoursesStore } from '../store/coursesStore';
import { useAuthStore } from '../store/authStore';
import { PlayCircle, Lock, Clock, Calendar, Users, ArrowLeft, Shield, AlertCircle, Loader } from 'lucide-react';

export default function CoursePage() {
  const { id } = useParams<{ id: string }>();
  const courseId = id ? parseInt(id) : null;
  
  const { 
    currentCourse, 
    courseVideos, 
    hasAccess, 
    isLoading, 
    error, 
    fetchCourse, 
    clearError 
  } = useCoursesStore();
  
  const { subscriptions } = useAuthStore();

  useEffect(() => {
    if (courseId) {
      fetchCourse(courseId);
    }
  }, [courseId, fetchCourse]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ar-SA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatDuration = (seconds: number | null) => {
    if (!seconds) return 'غير محدد';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const getCurrentSubscription = () => {
    return subscriptions.find(sub => 
      sub.course_id === courseId && sub.is_valid
    );
  };

  if (!courseId) {
    return <Navigate to="/courses" replace />;
  }

  if (isLoading) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-8 h-8 animate-spin text-amber-600 mx-auto mb-4" />
          <p className="text-stone-600">جاري تحميل تفاصيل الكورس...</p>
        </div>
      </div>
    );
  }

  if (error || !currentCourse) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-stone-900 mb-2">الكورس غير موجود</h2>
          <p className="text-stone-600 mb-6">
            {error || 'لم يتم العثور على الكورس المطلوب'}
          </p>
          <Link
            to="/courses"
            className="bg-amber-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-amber-700 transition-colors"
          >
            العودة للكورسات
          </Link>
        </div>
      </div>
    );
  }

  const currentSubscription = getCurrentSubscription();

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-stone-50 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Breadcrumb */}
        <div className="flex items-center space-x-2 rtl:space-x-reverse text-sm text-stone-500 mb-8">
          <Link to="/courses" className="hover:text-stone-700">
            الكورسات
          </Link>
          <ArrowLeft className="w-4 h-4 rtl:rotate-180" />
          <span>{currentCourse.title}</span>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-8 bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center space-x-2 rtl:space-x-reverse">
              <AlertCircle className="w-5 h-5 text-red-600" />
              <p className="text-red-700">{error}</p>
              <button
                onClick={clearError}
                className="mr-auto text-red-600 hover:text-red-700"
              >
                ×
              </button>
            </div>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Course Info */}
          <div className="lg:col-span-2">
            {/* Course Header */}
            <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-6">
                <div className="flex-1">
                  <h1 className="text-3xl md:text-4xl font-bold text-stone-900 mb-4">
                    {currentCourse.title}
                  </h1>
                  {currentCourse.description && (
                    <p className="text-lg text-stone-600 leading-relaxed">
                      {currentCourse.description}
                    </p>
                  )}
                </div>
                
                {currentCourse.thumbnail_url && (
                  <div className="w-full sm:w-32 h-32 bg-gradient-to-br from-amber-100 to-stone-100 rounded-xl overflow-hidden mt-4 sm:mt-0 sm:mr-6">
                    <img
                      src={currentCourse.thumbnail_url}
                      alt={currentCourse.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
              </div>

              {/* Course Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                <div className="bg-stone-50 rounded-lg p-4">
                  <PlayCircle className="w-6 h-6 text-amber-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-stone-900">
                    {courseVideos.length}
                  </p>
                  <p className="text-sm text-stone-600">مقطع فيديو</p>
                </div>
                
                <div className="bg-stone-50 rounded-lg p-4">
                  <Clock className="w-6 h-6 text-amber-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-stone-900">
                    {currentCourse.duration_months}
                  </p>
                  <p className="text-sm text-stone-600">شهر</p>
                </div>
                
                <div className="bg-stone-50 rounded-lg p-4">
                  <Users className="w-6 h-6 text-amber-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-stone-900">
                    {currentCourse.active_subscriptions || 0}
                  </p>
                  <p className="text-sm text-stone-600">طالب مشترك</p>
                </div>
                
                <div className="bg-stone-50 rounded-lg p-4">
                  <Calendar className="w-6 h-6 text-amber-600 mx-auto mb-2" />
                  <p className="text-xs font-bold text-stone-900">
                    {formatDate(currentCourse.created_at)}
                  </p>
                  <p className="text-sm text-stone-600">تاريخ النشر</p>
                </div>
              </div>
            </div>

            {/* Course Content */}
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <h2 className="text-2xl font-bold text-stone-900 mb-6">محتوى الكورس</h2>
              
              {courseVideos.length === 0 ? (
                <div className="text-center py-12">
                  <PlayCircle className="w-16 h-16 text-stone-300 mx-auto mb-4" />
                  <p className="text-stone-500">لا توجد مقاطع فيديو متاحة في هذا الكورس</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {courseVideos.map((video, index) => (
                    <div
                      key={video.id}
                      className={`border rounded-lg p-4 transition-colors ${
                        hasAccess
                          ? 'border-stone-200 hover:border-amber-300 hover:bg-amber-50'
                          : 'border-stone-200 bg-stone-50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4 rtl:space-x-reverse">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            hasAccess ? 'bg-amber-100' : 'bg-stone-200'
                          }`}>
                            {hasAccess ? (
                              <PlayCircle className="w-5 h-5 text-amber-600" />
                            ) : (
                              <Lock className="w-5 h-5 text-stone-500" />
                            )}
                          </div>
                          
                          <div className="flex-1">
                            <h3 className={`font-semibold ${
                              hasAccess ? 'text-stone-900' : 'text-stone-500'
                            }`}>
                              {index + 1}. {video.title}
                            </h3>
                            {video.description && (
                              <p className={`text-sm mt-1 ${
                                hasAccess ? 'text-stone-600' : 'text-stone-400'
                              }`}>
                                {video.description}
                              </p>
                            )}
                          </div>
                        </div>
                        
                        <div className="text-left rtl:text-right">
                          {video.duration_seconds && (
                            <p className={`text-sm ${
                              hasAccess ? 'text-stone-600' : 'text-stone-400'
                            }`}>
                              {formatDuration(video.duration_seconds)}
                            </p>
                          )}
                        </div>
                      </div>
                      
                      {!hasAccess && (
                        <div className="mt-3 flex items-center space-x-2 rtl:space-x-reverse text-sm text-stone-500">
                          <Shield className="w-4 h-4" />
                          <span>يتطلب اشتراك نشط لمشاهدة هذا المحتوى</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Subscription Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-lg p-8 sticky top-8">
              {hasAccess && currentSubscription ? (
                /* Active Subscription */
                <div className="text-center">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <PlayCircle className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="text-xl font-bold text-stone-900 mb-2">
                    لديك اشتراك نشط
                  </h3>
                  <p className="text-stone-600 mb-4">
                    يمكنك مشاهدة جميع مقاطع الفيديو في هذا الكورس
                  </p>
                  
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-stone-600">الاشتراك ينتهي في:</span>
                      <span className="font-bold text-green-700">
                        {currentSubscription.days_remaining} يوم
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm mt-2">
                      <span className="text-stone-600">تاريخ الانتهاء:</span>
                      <span className="font-semibold text-stone-900">
                        {formatDate(currentSubscription.end_date)}
                      </span>
                    </div>
                  </div>
                  
                  <button className="w-full bg-green-600 text-white py-3 rounded-lg font-semibold">
                    بدء المشاهدة
                  </button>
                </div>
              ) : (
                /* No Access */
                <div className="text-center">
                  <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Lock className="w-8 h-8 text-amber-600" />
                  </div>
                  <h3 className="text-xl font-bold text-stone-900 mb-2">
                    يتطلب اشتراك
                  </h3>
                  <p className="text-stone-600 mb-6">
                    تحتاج إلى سيريال نمبر صالح للوصول إلى محتوى هذا الكورس
                  </p>
                  
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                    <div className="flex items-center justify-between text-sm mb-2">
                      <span className="text-stone-600">مدة الاشتراك:</span>
                      <span className="font-bold text-amber-700">
                        {currentCourse.duration_months} شهر
                      </span>
                    </div>
                    {currentCourse.price > 0 && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-stone-600">السعر:</span>
                        <span className="font-bold text-amber-700">
                          {currentCourse.price} ر.س
                        </span>
                      </div>
                    )}
                  </div>
                  
                  <Link
                    to="/profile"
                    className="w-full bg-amber-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-amber-700 transition-colors block text-center"
                  >
                    تفعيل السيريال نمبر
                  </Link>
                  
                  <p className="text-xs text-stone-500 mt-4">
                    لديك سيريال نمبر؟ فعله من ملفك الشخصي للوصول إلى المحتوى
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}